// ** Redux Imports
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { isNil } from 'lodash-es';

import { getErrorCode, getErrorMessage } from '@/utils/errors';
import { readManualLanguagePick } from '@/nova/config/manualLanguagePick';
import { toBackendLang } from '@/nova/config/langCodeMap';
import {
  isSsoEmailRequired,
  isSsoLogin,
  postGoogle,
  postTelegram,
  postTelegramComplete,
  type SsoGoogleBody,
  type SsoLoginOptions,
  type SsoLoginResponse,
  type SsoTelegramBody,
  type SsoTelegramCompleteBody,
} from '@/nova/pages/social-login/ssoClient';

import type { LogoutOptions } from '../visitor-context-teardown';

import { RootState } from '../';
import { userBrandsList } from './casino-instance';
import { sessionLoad, updateSessionPostAuth } from './session';
import { logoutUserSession } from './userSessions';

const DEBUG = false;

type GenericError = {
  rejectValue: {
    error: string;
  };
};

/**
 * The support agent (admin) running an impersonated session. Present on the
 * `/auth/check` response only while an admin is signed in AS this account; absent
 * for an ordinary owner session. Drives the studio's impersonation banner.
 */
export type AdminImpersonator = {
  user_id: string;
  username: string;
};

type UserData = {
  email: string;
  user_id: string;
  email_verified?: boolean;
  admin?: AdminImpersonator | null;
};

type AuthenticationData = {
  token: string;
  refresh: string;
  expires: string;
  user: UserData;
  success: boolean;
};

const apiUrl = window.config.userApiUrl + '/auth';

type AuthenticationLoginProps = {
  username: string;
  password: string;
};

type AuthenticationLoginResult = {
  data: AuthenticationData;
  success: boolean;
};

/**
 * @deprecated
 * No longer used, kept for reference
 */
export const authenticationLogin = createAsyncThunk<AuthenticationLoginResult, AuthenticationLoginProps, GenericError>(
  'authentication/login',
  async ({ username, password }, { dispatch, getState, rejectWithValue }) => {
    try {
      const state: RootState = getState() as RootState;

      const response = await axios.post<AuthenticationData>(`${apiUrl}/login`, {
        username,
        password,
      });

      if (!isNil(response.data)) {
        return { data: response.data, success: true };
      }

      return rejectWithValue({
        error: "Couldn't login with provided credentials",
      });
    } catch (error) {
      return rejectWithValue({
        error: getErrorMessage(error),
      });
    }
  },
);

type AuthenticationRefreshProps = undefined;

type AuthenticationRefreshResult = {
  data: AuthenticationData;
  success: boolean;
};

export const authenticationRefresh = createAsyncThunk<
  AuthenticationRefreshResult,
  AuthenticationRefreshProps,
  GenericError
>('authentication/refresh', async (_args, { rejectWithValue, getState }) => {
  try {
    const state: RootState = getState() as RootState;
    const token = state.authentication.refresh_token;

    if (isNil(token)) {
      return {
        data: { token: '', refresh: '', expires: '', user: { email: '', user_id: '' }, success: false },
        success: false,
      };
    }

    const response = await axios.post<AuthenticationData>(`${apiUrl}/refresh`, {
      token,
    });

    if (!isNil(response.data)) {
      return { data: response.data, success: true };
    }

    return rejectWithValue({
      error: "Couldn't refresh auth token",
    });
  } catch (error) {
    return rejectWithValue({
      error: getErrorMessage(error),
    });
  }
});

type AuthenticationCheckProps = undefined;

type AuthenticationCheckResult = {
  data: UserData;
  success: boolean;
  /** The access token this check validated. The fulfilled reducer applies the result
   *  only when this is STILL the current token — so a check for a token that a
   *  concurrent magic-link sign-in (impersonation) has already replaced cannot
   *  overwrite the fresh identity with the previous one. Mirrors the rejected guard. */
  checkedToken?: string | null;
};

type AuthenticationCheckError = {
  rejectValue: {
    error: string;
    /** The access token this check validated. The rejected reducer only tears the session
     *  down when this is STILL the current token — so a 401 for a token that a concurrent
     *  magic-link sign-in / refresh has already replaced cannot wipe the fresh, valid one. */
    checkedToken?: string | null;
  };
};

export const authenticationCheck = createAsyncThunk<
  AuthenticationCheckResult,
  AuthenticationCheckProps,
  AuthenticationCheckError
>('authentication/check', async (_args, { rejectWithValue, getState }) => {
  const state: RootState = getState() as RootState;
  const token = state.authentication.access_token;
  try {
    if (isNil(token)) {
      return { data: { email: '', user_id: '' }, success: false };
    }

    const response = await axios.post<UserData>(`${apiUrl}/check`, {
      token,
    });

    if (!isNil(response.data)) {
      return { data: response.data, success: true, checkedToken: token };
    }

    return rejectWithValue({
      error: "Couldn't check auth token",
      checkedToken: token,
    });
  } catch (error) {
    return rejectWithValue({
      error: getErrorMessage(error),
      checkedToken: token,
    });
  }
});

type AuthenticationRegisterProps = {
  username: string;
  session_id: string;
};

type AuthenticationRegisterResult = {
  data: AuthenticationData;
  success: boolean;
};

/**
 * @deprecated
 * No longer used, kept for reference
 */
export const authenticationRegister = createAsyncThunk<
  AuthenticationRegisterResult,
  AuthenticationRegisterProps,
  GenericError
>('authentication/register', async ({ username, session_id }, { rejectWithValue }) => {
  try {
    const response = await axios.post<AuthenticationData>(`${apiUrl}/register`, {
      username,
      session_id,
    });

    if (!isNil(response.data)) {
      return { data: response.data, success: true };
    }

    return rejectWithValue({
      error: "Couldn't register with provided data",
    });
  } catch (error) {
    return rejectWithValue({
      error: getErrorMessage(error),
    });
  }
});

export type AuthenticationLinkData = AuthenticationData & {
  /**
   * Optional, not guaranteed: a redeemed support-impersonation link comes back with no
   * `session_id` at all (verified against spark-test). `sessionLoad` already treats a
   * missing id as a benign no-op load rather than minting, so the sign-in still settles;
   * the type just says so now instead of promising a string that is not always there.
   */
  session_id?: string;
  /**
   * Where the auth service wants THIS sign-in to land, as an absolute url on the deployed
   * host (e.g. `https://owners-hub.<env>.newtoncelestial.com/account/home`). It takes
   * precedence over the app's own brand-state routing, so a support impersonation link (or
   * any link minted for a specific page) opens where it was aimed. Optional: a link that
   * predates the field, or any flow the backend has no destination for, simply omits it and
   * the caller falls back to `resolvePostAuthRoute`. Turned into a navigable in-app path by
   * `utils/login-redirect.ts` (never followed cross-origin).
   */
  redirect_url?: string | null;
};

type AuthenticationLinkProps = {
  link: string;
};

type AuthenticationLinkResult = {
  data: AuthenticationLinkData;
  success: boolean;
};

// Like GenericError, but also carries the API's machine-readable error code so the
// /login page can tell WHY a link redemption failed and surface a targeted notice
// (e.g. `LOGIN_LINK_EXPIRED` → the "your link has expired" box above the email field)
// instead of silently dumping the user back on a blank form.
type AuthenticationLinkError = {
  rejectValue: {
    error: string;
    code?: string;
  };
};

export const authenticationLink = createAsyncThunk<AuthenticationLinkResult, AuthenticationLinkProps, AuthenticationLinkError>(
  'authentication/link',
  async ({ link }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.get<AuthenticationLinkData>(`${apiUrl}/link/${encodeURIComponent(link)}`);

      if (isNil(response.data)) {
        return rejectWithValue({
          error: "Couldn't get linked data",
        });
      }

      // AWAIT the two follow-up fetches the /login routing gate depends on, and fail the link
      // thunk if either rejects. Previously these were fire-and-forget and the thunk returned
      // success immediately, so a failed brands/session fetch left `authentication.error` null
      // and the /login spinner never cleared (the 401 interceptor is suppressed on /login, so
      // there was no escape). Awaiting + propagating routes the failure through
      // `authentication.error`, which the login page already surfaces. The token is passed
      // explicitly so these don't depend on the fulfilled reducer having stored it yet.
      await Promise.all([
        dispatch(userBrandsList({ token: response.data.token })).unwrap(),
        dispatch(
          sessionLoad({
            id: response.data.session_id,
            email: response.data.user.email,
            token: response.data.token,
            // The link's embedded session_id is often a STALE, already-advanced build. Load it
            // non-owningly so a non-resumable one (stuck `payment_successful`, done, cancelled)
            // is DROPPED here instead of re-adopted on EVERY relogin — the recurring "finish
            // setup" banner + configurator bounce + onboarding 500 the owner kept hitting. A
            // genuine `configuration` build still resumes.
            ambient: true,
          }),
        ).unwrap(),
      ]);

      return { data: response.data, success: true };
    } catch (error) {
      return rejectWithValue({
        error: getErrorMessage(error),
        // Surface the raw code (e.g. `LOGIN_LINK_EXPIRED`) so /login can branch on it.
        code: getErrorCode(error),
      });
    }
  },
);

type AuthenticationExistingAccountData = AuthenticationData & {
  session_id: string;
  success: true;
};

type AuthenticationNewAccountData = {
  message: string;
  success: false;
};

type AuthenticationAccountData = AuthenticationExistingAccountData | AuthenticationNewAccountData;

type AuthenticationLoginEmailProps = {
  email: string;
  sessionId: string;
};

type AuthenticationLoginEmailResult = {
  data: AuthenticationAccountData;
  success: boolean;
};

export const authenticationLoginEmail = createAsyncThunk<
  AuthenticationLoginEmailResult,
  AuthenticationLoginEmailProps,
  GenericError
>('authentication/loginEmail', async ({ email, sessionId }, { dispatch, getState, rejectWithValue }) => {
  try {
    // Send the active UI language (short code: 'en', 'mx', …) so the backend can localise
    // the magic-link email and persist the new owner's language on the account. This call is
    // shared by both /register and /login, so both send it. Prefer the EXPLICIT in-app pick
    // recorded this session (nova/config/manualLanguagePick), the source of truth for a fresh
    // mint, falling back to `application.language` (kept in sync with i18next). Both hold the APP
    // i18n code, so CONVERT it to the backend language code before sending (`toBackendLang`:
    // `bra`→`br`, `arg`→`ar`, `za`→`af`; every other code passes through) — the backend/account
    // stores the country/language code, never the Intl-safe app folder id. Either may carry a
    // region-tagged form ('en-US'), so normalise to the short code first and omit the param entirely
    // when it's unknown rather than send an empty value.
    const state = getState() as RootState;
    const rawLanguage = readManualLanguagePick() ?? state.application.language;
    const language = typeof rawLanguage === 'string' ? toBackendLang(rawLanguage.split('-')[0]) : undefined;

    const response = await axios.get<AuthenticationAccountData>(`${apiUrl}/login-email`, {
      params: {
        email,
        session_id: sessionId,
        ...(language ? { language } : {}),
      },
    });

    if (!isNil(response.data)) {
      if (response.data.success) {
        // New account
        dispatch(updateSessionPostAuth('redirect'));
      } else {
        // Existing account
        dispatch(updateSessionPostAuth('mail'));
      }

      return { data: response.data, success: true };
    }

    return rejectWithValue({
      error: "Couldn't get linked data",
    });
  } catch (error) {
    return rejectWithValue({
      error: getErrorMessage(error),
    });
  }
});

type AuthenticationLogoutResult = {
  success: boolean;
};

/**
 * Log the current device out of the auth service, then resolve regardless of the
 * network outcome. Callers always follow up with the local `logout` reducer (clears
 * tokens + localStorage) and a redirect, so a failed revoke never traps the user in a
 * logged-in shell.
 *
 * There is NO `/auth/logout` endpoint (it 404s); server-side revocation lives on the
 * sessions API. So this revokes the browser's own tokens (access + refresh) via
 * `logoutUserSession` (`GET {userApiUrl}/auth/sessions/logout?token=…`) — the SAME path
 * the Settings "Sign out of this device" action uses, so both logouts behave identically.
 */
export const authenticationLogout = createAsyncThunk<AuthenticationLogoutResult, void>(
  'authentication/logoutRequest',
  async (_arg, { dispatch, getState }) => {
    const state: RootState = getState() as RootState;
    const { access_token, refresh_token } = state.authentication;
    const tokens = [access_token, refresh_token].filter((token): token is string => !!token);
    await Promise.all(
      tokens.map((token) =>
        dispatch(logoutUserSession({ token }))
          .unwrap()
          .catch(() => undefined),
      ),
    );
    return { success: true };
  },
);

export type AuthenticationLoginPasswordProps = {
  email: string;
  password: string;
};

type AuthenticationLoginPasswordResult = {
  data: AuthenticationLinkData;
  success: boolean;
};

export const authenticationLoginPassword = createAsyncThunk<
  AuthenticationLoginPasswordResult,
  AuthenticationLoginPasswordProps,
  AuthenticationLinkError
>('authentication/loginPassword', async ({ email, password }, { dispatch, rejectWithValue }) => {
  try {
    // The real backend keys password sign-in on `username`, NOT `email` (the shipped
    // micros-wl_owner_hub `POST /user/auth/login` contract). Owners are identified by email
    // everywhere in this app (the magic-link flow is email-based), so the email the form
    // collects is sent AS the username. The BE rejects with `INVALID_CREDENTIALS` (used for
    // unknown user, missing and wrong password alike, no enumeration), and also `USER_DISABLED`
    // / `LOGIN_NOT_ALLOWED` — all read by `getErrorCode` regardless of HTTP status; the /login
    // page surfaces INVALID_CREDENTIALS specifically and the other two via the generic notice.
    const response = await axios.post<AuthenticationLinkData>(`${apiUrl}/login`, {
      username: email,
      password,
    });

    if (isNil(response.data)) {
      return rejectWithValue({
        error: "Couldn't login with provided credentials",
      });
    }

    // AWAIT the two follow-up fetches the /login routing gate depends on, mirroring
    // authenticationLink — see the comment there for why these are awaited rather
    // than fire-and-forget.
    await Promise.all([
      dispatch(userBrandsList({ token: response.data.token })).unwrap(),
      dispatch(
        sessionLoad({
          id: response.data.session_id,
          email: response.data.user.email,
          token: response.data.token,
          ambient: true,
        }),
      ).unwrap(),
    ]);

    return { data: response.data, success: true };
  } catch (error) {
    return rejectWithValue({
      error: getErrorMessage(error),
      code: getErrorCode(error),
    });
  }
});

// ── SSO (Google / Telegram) ────────────────────────────────────────────────
// Backend: micros-wl_owner_hub `POST /user/auth/sso/*` (sso.md). SSO never mints its own token — the
// callback returns the standard LoginResponse, so these thunks settle exactly like `authenticationLink`
// / `authenticationLoginPassword`: on success they AWAIT the same brands + session follow-ups the
// /login routing gate depends on, then `applyAuthSuccess` runs in the fulfilled reducer. The two
// non-token outcomes (a "login link sent to email" for a stale/existing account, and Telegram's
// "email required" for an unknown identity) carry NO token and MUST NOT hit `applyAuthSuccess`.

/** Adapt the BE LoginResponse to the app's `AuthenticationLinkData` (admin comes back untyped from SSO). */
const ssoLoginToLinkData = (r: SsoLoginResponse): AuthenticationLinkData => ({
  token: r.token,
  refresh: r.refresh,
  expires: r.expires,
  success: true,
  session_id: r.session_id,
  redirect_url: r.redirect_url ?? null,
  user: {
    email: r.user.email,
    user_id: r.user.user_id,
    email_verified: r.user.email_verified,
    admin: (r.user.admin as AdminImpersonator | null) ?? null,
  },
});

/** The `/login` routing gate depends on brands + session being loaded; await both, like the other flows. */
type SsoDispatch = (action: ReturnType<typeof userBrandsList> | ReturnType<typeof sessionLoad>) => {
  unwrap: () => Promise<unknown>;
};
const settleSsoLogin = async (dispatch: SsoDispatch, data: AuthenticationLinkData): Promise<void> => {
  await Promise.all([
    dispatch(userBrandsList({ token: data.token })).unwrap(),
    dispatch(
      sessionLoad({ id: data.session_id, email: data.user.email, token: data.token, ambient: true }),
    ).unwrap(),
  ]);
};

/**
 * Every SSO login route accepts the same optional extras as `/register` and `/login-email`:
 * `session_id` (binds the visitor's onboarding session to the account, so a configured brand is
 * not orphaned) and `language` (localises the confirmation / welcome email and persists the new
 * owner's language) — see sso.md → "Flows".
 *
 * `language` is filled in here from the store (unaffected by the sign-in flow). `session_id` is NOT:
 * both SSO entry points `logout({keepVisitorContext:true})` before signing in, and that teardown
 * clears the onboarding session id from BOTH the store and localStorage (see session.ts
 * `clearSessionData`). So the caller must CAPTURE it before that logout and pass it in `body`
 * (SocialLoginBar reads it pre-logout; the Telegram path carries it across the redirect on the
 * stored attempt). Reading it from state here would always be null.
 */
const withSsoLoginOptions = <T extends SsoLoginOptions>(body: T, state: RootState): T => {
  const rawLanguage = readManualLanguagePick() ?? state.application.language;
  const language = typeof rawLanguage === 'string' ? toBackendLang(rawLanguage.split('-')[0]) : undefined;
  return {
    ...body,
    ...(language ? { language } : {}),
  };
};

/** Success carries `data` (drives `applyAuthSuccess`); the other outcomes are token-less. */
export type AuthenticationSsoResult =
  | { data: AuthenticationLinkData; success: true }
  | { linkSent: true; success: false }
  | { needsEmail: true; ssoTicket: string; success: false };

export const authenticationSsoGoogle = createAsyncThunk<
  AuthenticationSsoResult,
  SsoGoogleBody,
  AuthenticationLinkError
>('authentication/ssoGoogle', async (body, { dispatch, getState, rejectWithValue }) => {
  try {
    const result = await postGoogle(withSsoLoginOptions(body, getState() as RootState));
    if (isSsoLogin(result)) {
      const data = ssoLoginToLinkData(result);
      await settleSsoLogin(dispatch as unknown as SsoDispatch, data);
      return { data, success: true };
    }
    // { success: false, message: "Login link sent to email" } — a stale/existing account.
    return { linkSent: true, success: false };
  } catch (error) {
    return rejectWithValue({ error: getErrorMessage(error), code: getErrorCode(error) });
  }
});

export const authenticationSsoTelegram = createAsyncThunk<
  AuthenticationSsoResult,
  SsoTelegramBody,
  AuthenticationLinkError
>('authentication/ssoTelegram', async (body, { dispatch, getState, rejectWithValue }) => {
  try {
    const result = await postTelegram(withSsoLoginOptions(body, getState() as RootState));
    if (isSsoLogin(result)) {
      const data = ssoLoginToLinkData(result);
      await settleSsoLogin(dispatch as unknown as SsoDispatch, data);
      return { data, success: true };
    }
    if (isSsoEmailRequired(result)) {
      return { needsEmail: true, ssoTicket: result.sso_ticket, success: false };
    }
    return { linkSent: true, success: false };
  } catch (error) {
    return rejectWithValue({ error: getErrorMessage(error), code: getErrorCode(error) });
  }
});

export const authenticationSsoTelegramComplete = createAsyncThunk<
  AuthenticationSsoResult,
  SsoTelegramCompleteBody,
  AuthenticationLinkError
>('authentication/ssoTelegramComplete', async (body, { dispatch, getState, rejectWithValue }) => {
  try {
    const result = await postTelegramComplete(withSsoLoginOptions(body, getState() as RootState));
    if (isSsoLogin(result)) {
      const data = ssoLoginToLinkData(result);
      await settleSsoLogin(dispatch as unknown as SsoDispatch, data);
      return { data, success: true };
    }
    return { linkSent: true, success: false };
  } catch (error) {
    return rejectWithValue({ error: getErrorMessage(error), code: getErrorCode(error) });
  }
});

export type AuthenticationPasswordResetRequestProps = {
  email: string;
};

/**
 * SPB-480: request a password-reset / set-password link for `email`.
 *
 * `POST {userApiUrl}/auth/forgot-password` with `{ email }` (the shipped micros-wl_owner_hub
 * contract). Success is uniform whether or not the account exists (no enumeration) — the FE's
 * single "if an account exists, a link is on the way" confirmation already matches. On a matching
 * account the BE emails a link to `{ownerHubFrontUrl}/reset_password?code=<code>` (notification
 * 10010); the code is a stateless AES token valid 1 hour and single-use.
 *
 * This is NOT an auth event: requesting a reset link never signs anyone in, so it
 * has NO extraReducers and touches neither `applyAuthSuccess` nor the session. Callers `.unwrap()`
 * and drive their own local UI state (the confirmation view on `/reset-password`). The response is
 * deliberately non-committal about whether the email exists, so the page shows the same "if an
 * account exists, a link is on the way" confirmation either way (no account-enumeration signal).
 */
export const authenticationPasswordResetRequest = createAsyncThunk<
  { success: boolean },
  AuthenticationPasswordResetRequestProps,
  GenericError
>('authentication/passwordResetRequest', async ({ email }, { rejectWithValue }) => {
  try {
    await axios.post(`${apiUrl}/forgot-password`, { email });
    return { success: true };
  } catch (error) {
    return rejectWithValue({
      error: getErrorMessage(error),
    });
  }
});

export type AuthenticationPasswordResetProps = {
  code: string;
  new_password: string;
};

/**
 * SPB-480 (redemption): set a new password from an emailed forgot-password code.
 *
 * `POST {userApiUrl}/auth/reset-password` with `{ code, new_password }` (the shipped
 * micros-wl_owner_hub contract). `new_password` must be 8-128 chars (enforced client-side too).
 * Rejections: `INVALID_CODE` (malformed code) or `RESET_CODE_EXPIRED` (older than 1 hour, or the
 * code was already consumed / the password changed since it was issued — single-use). The FE shows
 * one generic "invalid or expired" notice and does not branch on the code (Decision D: the copy
 * already covers both; add branching only if product asks).
 *
 * Deliberately NOT an auth event: the BE returns no tokens and does not sign the user in — a
 * forgot-password reset in fact invalidates ALL of the user's sessions server-side. So (unlike
 * `authenticationLoginPassword`) this has NO extraReducers and touches neither `applyAuthSuccess`
 * nor the session; on success the caller sends the owner to `/login` to authenticate with the new
 * password. Mirrors `authenticationPasswordResetRequest` (the request half); callers `.unwrap()`
 * and drive their own local UI (the confirmation view on `/reset-password`).
 */
export const authenticationPasswordReset = createAsyncThunk<
  { success: boolean },
  AuthenticationPasswordResetProps,
  GenericError
>('authentication/passwordReset', async ({ code, new_password }, { rejectWithValue }) => {
  try {
    await axios.post(`${apiUrl}/reset-password`, { code, new_password });
    return { success: true };
  } catch (error) {
    return rejectWithValue({
      error: getErrorMessage(error),
    });
  }
});

/**
 * SPB-480 (authenticated change-password, request half): ask the BE to email a
 * change-password confirmation code to the signed-in owner.
 *
 * `GET {userApiUrl}/auth/change-password-request`, authenticated with the RAW access token
 * in the `Authorization` header (NOT `Bearer <token>`) — mirrors `getAuthHeaders` in
 * `userSessions.ts`, which is the header convention this app's authenticated user-API calls use.
 * Rejects early with a generic error when there is no token in state.
 *
 * NOT an auth event: it neither signs anyone in nor out, so it has NO extraReducers and touches
 * neither `applyAuthSuccess` nor the session — the current session survives (a subsequent confirm
 * invalidates only the owner's OTHER sessions server-side). The global 401 interceptor excludes
 * `/auth/` paths, so an auth failure here surfaces to the caller's local UI rather than triggering
 * a global logout; callers `.unwrap()` and drive their own confirmation view.
 */
export const authenticationChangePasswordRequest = createAsyncThunk<
  { success: boolean },
  void,
  GenericError
>('authentication/changePasswordRequest', async (_, { getState, rejectWithValue }) => {
  const authToken = (getState() as RootState).authentication.access_token;
  if (isNil(authToken)) return rejectWithValue({ error: 'No authentication token found' });
  try {
    await axios.get(`${apiUrl}/change-password-request`, { headers: { Authorization: authToken } });
    return { success: true };
  } catch (error) {
    return rejectWithValue({
      error: getErrorMessage(error),
    });
  }
});

export type AuthenticationChangePasswordConfirmProps = {
  code: string;
  new_password: string;
};

/**
 * SPB-480 (authenticated change-password, confirm half): set a new password using the
 * confirmation code emailed by `authenticationChangePasswordRequest`.
 *
 * `POST {userApiUrl}/auth/change-password-confirm` with `{ code, new_password }`, authenticated
 * with the RAW access token in the `Authorization` header (NOT `Bearer <token>`) — mirrors
 * `getAuthHeaders` in `userSessions.ts`, the header convention for this app's authenticated
 * user-API calls. Rejects early with a generic error when there is no token in state; on a BE
 * failure the reject carries the machine `code` (via `getErrorCode`) so the caller can branch.
 *
 * NOT an auth event: it returns no tokens and neither signs anyone in nor out, so it has NO
 * extraReducers and touches neither `applyAuthSuccess` nor the session — the current session
 * survives (the BE invalidates only the owner's OTHER sessions). The global 401 interceptor
 * excludes `/auth/` paths, so an auth failure here surfaces to the caller's local UI rather than
 * triggering a global logout; callers `.unwrap()` and drive their own confirmation view.
 */
export const authenticationChangePasswordConfirm = createAsyncThunk<
  { success: boolean },
  AuthenticationChangePasswordConfirmProps,
  AuthenticationLinkError
>('authentication/changePasswordConfirm', async ({ code, new_password }, { getState, rejectWithValue }) => {
  const authToken = (getState() as RootState).authentication.access_token;
  if (isNil(authToken)) return rejectWithValue({ error: 'No authentication token found' });
  try {
    await axios.post(
      `${apiUrl}/change-password-confirm`,
      { code, new_password },
      { headers: { Authorization: authToken } },
    );
    return { success: true };
  } catch (error) {
    return rejectWithValue({
      error: getErrorMessage(error),
      code: getErrorCode(error),
    });
  }
});

export type AuthenticationRegisterPasswordProps = {
  email: string;
  password: string;
  sessionId: string;
};

type AuthenticationRegisterPasswordResult = {
  data: AuthenticationLinkData;
  success: boolean;
};

/**
 * SPB-352 (register-password): create an account with a password AND sign in, in one step.
 *
 * DORMANT — NO BE ENDPOINT. The shipped micros-wl_owner_hub backend has no
 * `/auth/register-password`, and `POST /auth/register` accepts only `{ username, session_id? }`
 * (NO password): accounts get a random, unseen password and set a real one only via the
 * forgot-password -> reset-password flow or the authenticated change-password flow. So
 * "register with a password in one step" has no server support today. This thunk is parked,
 * pending a BE decision to add a register-with-password endpoint (requested for SPB-352); the
 * Password mode on `/register` is hidden in the meantime (a one-line unhide when BE ships).
 * Do NOT point this at `/auth/register` — that endpoint silently ignores the password and would
 * sign the owner in with a random one they can never reproduce. The assumed shape below
 * (`{ username, password, session_id, language? }` -> `AuthenticationLinkData`) is what the
 * eventual endpoint is expected to mirror (the page mints a session FIRST to preserve the
 * visitor-context / marketing hand-off and sends its id; the backend does not mint).
 *
 * This IS an auth event: unlike magic-link sign-up, a password register signs the owner in
 * immediately, so — exactly like `authenticationLoginPassword` — it awaits the follow-up
 * `userBrandsList` + `sessionLoad` the routing gate depends on and its `fulfilled` reducer runs
 * `applyAuthSuccess`. `username` carries the email (owners are identified by email everywhere,
 * same rationale as loginPassword); `language` is sent so the new owner's language is persisted
 * (same source/normalisation as the shared magic-link `login-email` call).
 */
export const authenticationRegisterPassword = createAsyncThunk<
  AuthenticationRegisterPasswordResult,
  AuthenticationRegisterPasswordProps,
  AuthenticationLinkError
>('authentication/registerPassword', async ({ email, password, sessionId }, { dispatch, getState, rejectWithValue }) => {
  try {
    const state = getState() as RootState;
    const rawLanguage = readManualLanguagePick() ?? state.application.language;
    const language = typeof rawLanguage === 'string' ? toBackendLang(rawLanguage.split('-')[0]) : undefined;

    const response = await axios.post<AuthenticationLinkData>(`${apiUrl}/register-password`, {
      username: email,
      password,
      session_id: sessionId,
      ...(language ? { language } : {}),
    });

    if (isNil(response.data)) {
      return rejectWithValue({
        error: "Couldn't register with provided data",
      });
    }

    // AWAIT the two follow-up fetches the routing gate depends on, mirroring
    // authenticationLoginPassword / authenticationLink — see the comment there for why these are
    // awaited rather than fire-and-forget. A bad/dead token from a drifted BE contract rejects
    // here (401 on the follow-ups) and surfaces the error notice instead of routing on.
    await Promise.all([
      dispatch(userBrandsList({ token: response.data.token })).unwrap(),
      dispatch(
        sessionLoad({
          id: response.data.session_id,
          email: response.data.user.email,
          token: response.data.token,
          ambient: true,
        }),
      ).unwrap(),
    ]);

    return { data: response.data, success: true };
  } catch (error) {
    return rejectWithValue({
      error: getErrorMessage(error),
      code: getErrorCode(error),
    });
  }
});

export interface ConfigReducer {
  access_token: string | null;
  refresh_token: string | null;
  expires: string | null;
  auth_date: string | null;
  auth_type: string;
  user: {
    email: string | null;
    user_id: string | null;
    /**
     * Whether the account email is verified. `null` when unknown (a response that did not carry the
     * field). The verify-email banner shows only on an explicit `false` (an SSO signup that has not
     * confirmed its mailbox); `null`/`true` never show it. See sso.md.
     */
    email_verified: boolean | null;
    /** Set while a support agent is impersonating this account (see /auth/check); null otherwise. */
    admin: AdminImpersonator | null;
  };
  in_progress: boolean;
  error: string | void | null;
}

/**
 * Applies a successful token/user response to authentication state — the shared body
 * of `authenticationLink.fulfilled`, `authenticationLoginPassword.fulfilled`, and
 * `authenticationRegisterPassword.fulfilled` (the three real auth events)
 * (Decision C: NOT wired into the deprecated/divergent `authenticationLogin` /
 * `authenticationRegister` / `authenticationLoginEmail` / `authenticationRefresh`
 * fulfilled cases, which stay as-is).
 */
const applyAuthSuccess = (state: ConfigReducer, data: AuthenticationLinkData): void => {
  state.auth_type = 'user';
  state.access_token = data.token;
  state.refresh_token = data.refresh;
  state.expires = data.expires;
  state.auth_date = new Date().toISOString();
  state.user.email = data.user.email;
  state.user.user_id = data.user.user_id;
  // Read straight from the response: every LoginResponse carries it (verified in sso.md). `?? null`
  // preserves an explicit `false` (SSO signup pending mailbox confirmation) while leaving a response
  // that omits it as "unknown" (no banner). Do NOT default to `true`.
  state.user.email_verified = data.user.email_verified ?? null;
  state.user.admin = data.user.admin ?? null;
  localStorage.setItem('access_token', data.token);
  localStorage.setItem('refresh_token', data.refresh);
  localStorage.setItem('expires', data.expires);
  localStorage.setItem('auth_date', state.auth_date);
};

export const authenticationSlice = createSlice({
  name: 'authentication',
  initialState: <ConfigReducer>{
    access_token: localStorage.getItem('access_token') ?? null,
    refresh_token: localStorage.getItem('refresh_token') ?? null,
    expires: localStorage.getItem('expires') ?? null,
    auth_date: localStorage.getItem('auth_date') ?? null,
    auth_type: 'app',
    user: {
      email: null,
      user_id: null,
      email_verified: null,
      admin: null,
    },
    in_progress: false, // at page load this will be true because seeing if the user is logged in is the first thing we check
    error: null,
  },
  reducers: {
    /**
     * Tear this browser's session down locally (tokens + their localStorage copies). The
     * listener in `store/index.ts` extends it into the single teardown chokepoint, clearing the
     * session / brand / owner-scoped slices too.
     *
     * The optional payload only scopes what that listener drops: the register / sign-in funnel
     * passes `keepVisitorContext` so the marketing hand-off and the visitor's language pick
     * survive the sign-out it performs purely to keep the previous owner's token off the session
     * it is about to mint. See {@link LogoutOptions}. Server-side revocation is deliberately NOT
     * part of this: that belongs to `authenticationLogout`, so signing out here never kills the
     * owner's other devices.
     */
    logout: (state, _action: PayloadAction<LogoutOptions | undefined>) => {
      state.access_token = null;
      state.refresh_token = null;
      state.expires = null;
      state.auth_date = null;
      state.auth_type = 'app';
      state.user.email = null;
      state.user.user_id = null;
      state.user.admin = null;
      // Reset to the initial 'unknown' (not the stale value): an SSO-unverified session sets this
      // false, and without clearing it here a different, email-verified owner signing in on the same
      // tab (no reload) inherits `false` and wrongly sees the VerifyEmailBanner until /auth/check.
      state.user.email_verified = null;
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('expires');
      localStorage.removeItem('auth_date');
    },
    updateUser: (state, action) => {
      if (!isNil(action.payload.email)) state.user.email = action.payload.email;
      if (!isNil(action.payload.user_id)) state.user.email = action.payload.user_id;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authenticationLogin.fulfilled, (state, action) => {
        state.in_progress = false;
        if (action.payload.success) {
          state.auth_type = 'user';
          state.access_token = action.payload.data.token;
          state.refresh_token = action.payload.data.refresh;
          state.expires = action.payload.data.expires;
          state.auth_date = new Date().toISOString();
          state.user.email = action.payload.data.user.email;
          state.user.user_id = action.payload.data.user.user_id;
          state.user.admin = action.payload.data.user.admin ?? null;
          localStorage.setItem('access_token', action.payload.data.token);
          localStorage.setItem('refresh_token', action.payload.data.refresh);
          localStorage.setItem('expires', action.payload.data.expires);
          localStorage.setItem('auth_date', state.auth_date);
        }
        DEBUG && console.timeEnd('authenticationLogin.query');
        DEBUG && console.log('authenticationLogin.fulfilled', action.payload);
      })
      .addCase(authenticationLogin.pending, (state) => {
        DEBUG && console.time('authenticationLogin.query');
        state.in_progress = true;
        state.error = null;
      })
      .addCase(authenticationLogin.rejected, (state, action) => {
        state.in_progress = false;
        state.error = action.payload?.error;
        DEBUG && console.log('authenticationLogin.rejected', action.payload);
      })

      .addCase(authenticationRefresh.fulfilled, (state, action) => {
        state.in_progress = false;
        if (action.payload.success) {
          state.auth_type = 'user';
          state.access_token = action.payload.data.token;
          state.refresh_token = action.payload.data.refresh;
          state.expires = action.payload.data.expires;
          state.auth_date = new Date().toISOString();
          state.user.email = action.payload.data.user.email;
          state.user.user_id = action.payload.data.user.user_id;
          // Continuing session: only adopt `admin` when the refresh response carries
          // it, so a refresh (e.g. the auth-interceptor's mid-session 401 retry) that
          // omits the field can't clear the impersonation status /auth/check set.
          if (action.payload.data.user.admin !== undefined) {
            state.user.admin = action.payload.data.user.admin;
          }
          localStorage.setItem('access_token', action.payload.data.token);
          localStorage.setItem('refresh_token', action.payload.data.refresh);
          localStorage.setItem('expires', action.payload.data.expires);
          localStorage.setItem('auth_date', state.auth_date);
        }
        DEBUG && console.timeEnd('authenticationRefresh.query');
        DEBUG && console.log('authenticationRefresh.fulfilled', action.payload);
      })
      .addCase(authenticationRefresh.pending, (state) => {
        state.in_progress = true;
        DEBUG && console.time('authenticationRefresh.query');
      })
      .addCase(authenticationRefresh.rejected, (state, action) => {
        state.in_progress = false;
        state.error = action.payload?.error;

        DEBUG && console.log('authenticationRefresh.rejected', action.payload);
      })

      .addCase(authenticationCheck.fulfilled, (state, action) => {
        state.in_progress = false;
        if (action.payload.success) {
          // Ignore a result for a token that's no longer current. The boot check runs
          // against the localStorage token; a concurrent magic-link sign-in
          // (impersonation) can replace it with a fresh token for a DIFFERENT owner
          // before this check resolves. Applying the old token's user would overwrite
          // the fresh identity with the previous one (and leave user_id mismatched
          // with the live token). Mirrors the rejected handler's stale-token guard.
          const checkedToken = action.payload.checkedToken;
          if (checkedToken != null && state.access_token !== checkedToken) {
            return;
          }
          state.auth_type = 'user';
          state.user.email = action.payload.data.email;
          state.user.user_id = action.payload.data.user_id;
          // /auth/check returns email_verified (sso.md), so the banner survives a reload.
          state.user.email_verified = action.payload.data.email_verified ?? null;
          // /auth/check is the authority on impersonation: it returns `admin` only
          // while a support agent is signed in AS this account, so its presence
          // (or absence) definitively sets/clears the impersonation banner.
          state.user.admin = action.payload.data.admin ?? null;
        }
        DEBUG && console.timeEnd('authenticationCheck.query');
        DEBUG && console.log('authenticationCheck.fulfilled', action.payload);
      })
      .addCase(authenticationCheck.pending, (state) => {
        state.in_progress = true;
        DEBUG && console.time('authenticationCheck.query');
      })
      .addCase(authenticationCheck.rejected, (state, action) => {
        state.in_progress = false;

        // Ordering-safe teardown: only tear the session down if the token we just failed to
        // validate is STILL the current one. The boot check runs against the localStorage
        // token; a concurrent magic-link sign-in (or refresh) can replace it with a fresh,
        // valid token before this 401 lands. Wiping then would destroy the new token and
        // bounce a successful login back to the signed-out form. When the token has already
        // changed, this rejection is stale and irrelevant — leave tokens (and error) alone.
        const checkedToken = action.payload?.checkedToken;
        if (checkedToken != null && state.access_token !== checkedToken) {
          DEBUG && console.log('authenticationCheck.rejected (stale token, ignored)', action.payload);
          return;
        }

        state.error = action.payload?.error;

        state.access_token = null;
        state.refresh_token = null;
        state.expires = null;
        state.auth_date = null;
        state.auth_type = 'app';
        state.user.email = null;
        state.user.user_id = null;
        state.user.admin = null;
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('expires');
        localStorage.removeItem('auth_date');

        DEBUG && console.log('authenticationCheck.rejected', action.payload);
      })

      .addCase(authenticationRegister.fulfilled, (state, action) => {
        state.in_progress = false;
        if (action.payload.success) {
          state.auth_type = 'user';
          state.access_token = action.payload.data.token;
          state.refresh_token = action.payload.data.refresh;
          state.expires = action.payload.data.expires;
          state.auth_date = new Date().toISOString();
          state.user.email = action.payload.data.user.email;
          state.user.user_id = action.payload.data.user.user_id;
          state.user.admin = action.payload.data.user.admin ?? null;
          localStorage.setItem('access_token', action.payload.data.token);
          localStorage.setItem('refresh_token', action.payload.data.refresh);
          localStorage.setItem('expires', action.payload.data.expires);
          localStorage.setItem('auth_date', state.auth_date);
        }
        DEBUG && console.timeEnd('authenticationRegister.query');
        DEBUG && console.log('authenticationRegister.fulfilled', action.payload);
      })
      .addCase(authenticationRegister.pending, (state) => {
        DEBUG && console.time('authenticationRegister.query');
        state.in_progress = true;
        state.error = null;
      })
      .addCase(authenticationRegister.rejected, (state, action) => {
        state.in_progress = false;
        state.error = action.payload?.error;
        DEBUG && console.log('authenticationRegister.rejected', action.payload);
      })

      .addCase(authenticationLink.fulfilled, (state, action) => {
        state.in_progress = false;
        if (action.payload.success) {
          applyAuthSuccess(state, action.payload.data);
        }
        DEBUG && console.timeEnd('authenticationLink.query');
        DEBUG && console.log('authenticationLink.fulfilled', action.payload);
      })
      .addCase(authenticationLink.pending, (state) => {
        DEBUG && console.time('authenticationLink.query');
        state.in_progress = true;
        state.error = null;
      })
      .addCase(authenticationLink.rejected, (state, action) => {
        state.in_progress = false;
        state.error = action.payload?.error;
        DEBUG && console.log('authenticationLink.rejected', action.payload);
      })

      .addCase(authenticationLoginPassword.fulfilled, (state, action) => {
        state.in_progress = false;
        if (action.payload.success) {
          applyAuthSuccess(state, action.payload.data);
        }
        DEBUG && console.timeEnd('authenticationLoginPassword.query');
        DEBUG && console.log('authenticationLoginPassword.fulfilled', action.payload);
      })
      .addCase(authenticationLoginPassword.pending, (state) => {
        DEBUG && console.time('authenticationLoginPassword.query');
        state.in_progress = true;
        state.error = null;
      })
      .addCase(authenticationLoginPassword.rejected, (state, action) => {
        state.in_progress = false;
        state.error = action.payload?.error;
        DEBUG && console.log('authenticationLoginPassword.rejected', action.payload);
      })

      // SSO — success applies the session; linkSent / needsEmail carry no token (handled by the caller).
      .addCase(authenticationSsoGoogle.fulfilled, (state, action) => {
        state.in_progress = false;
        if (action.payload.success) {
          applyAuthSuccess(state, action.payload.data);
        }
      })
      .addCase(authenticationSsoGoogle.pending, (state) => {
        state.in_progress = true;
        state.error = null;
      })
      .addCase(authenticationSsoGoogle.rejected, (state, action) => {
        state.in_progress = false;
        state.error = action.payload?.error;
      })

      .addCase(authenticationSsoTelegram.fulfilled, (state, action) => {
        state.in_progress = false;
        if (action.payload.success) {
          applyAuthSuccess(state, action.payload.data);
        }
      })
      .addCase(authenticationSsoTelegram.pending, (state) => {
        state.in_progress = true;
        state.error = null;
      })
      .addCase(authenticationSsoTelegram.rejected, (state, action) => {
        state.in_progress = false;
        state.error = action.payload?.error;
      })

      .addCase(authenticationSsoTelegramComplete.fulfilled, (state, action) => {
        state.in_progress = false;
        if (action.payload.success) {
          applyAuthSuccess(state, action.payload.data);
        }
      })
      .addCase(authenticationSsoTelegramComplete.pending, (state) => {
        state.in_progress = true;
        state.error = null;
      })
      .addCase(authenticationSsoTelegramComplete.rejected, (state, action) => {
        state.in_progress = false;
        state.error = action.payload?.error;
      })

      .addCase(authenticationRegisterPassword.fulfilled, (state, action) => {
        state.in_progress = false;
        if (action.payload.success) {
          applyAuthSuccess(state, action.payload.data);
        }
        DEBUG && console.timeEnd('authenticationRegisterPassword.query');
        DEBUG && console.log('authenticationRegisterPassword.fulfilled', action.payload);
      })
      .addCase(authenticationRegisterPassword.pending, (state) => {
        DEBUG && console.time('authenticationRegisterPassword.query');
        state.in_progress = true;
        state.error = null;
      })
      .addCase(authenticationRegisterPassword.rejected, (state, action) => {
        state.in_progress = false;
        state.error = action.payload?.error;
        DEBUG && console.log('authenticationRegisterPassword.rejected', action.payload);
      })

      .addCase(authenticationLoginEmail.fulfilled, (state, action) => {
        state.in_progress = false;
        if (action.payload.success && action.payload.data.success) {
          state.auth_type = 'user';
          state.access_token = action.payload.data.token;
          state.refresh_token = action.payload.data.refresh;
          state.expires = action.payload.data.expires;
          state.auth_date = new Date().toISOString();
          state.user.email = action.payload.data.user.email;
          state.user.user_id = action.payload.data.user.user_id;
          state.user.admin = action.payload.data.user.admin ?? null;
          localStorage.setItem('access_token', action.payload.data.token);
          localStorage.setItem('refresh_token', action.payload.data.refresh);
          localStorage.setItem('expires', action.payload.data.expires);
          localStorage.setItem('auth_date', state.auth_date);
        }
        DEBUG && console.timeEnd('authenticationLoginEmail.query');
        DEBUG && console.log('authenticationLoginEmail.fulfilled', action.payload);
      })
      .addCase(authenticationLoginEmail.pending, (state) => {
        DEBUG && console.time('authenticationLoginEmail.query');
        state.in_progress = false;
        state.error = null;
      })
      .addCase(authenticationLoginEmail.rejected, (state, action) => {
        state.in_progress = false;
        state.error = action.payload?.error;
        DEBUG && console.log('authenticationLoginEmail.rejected', action.payload);
      });
  },
});

export const { logout, updateUser } = authenticationSlice.actions;

export default authenticationSlice.reducer;
